import { Controller } from '@hotwired/stimulus';

/**
 * Prorata Expenses Preview Controller
 *
 * Wires the "Total des dépenses communes HT" input in the
 * `_declare_expenses_modal` to the "Aperçu du calcul / Peines et soins"
 * read-only line so the architect sees the projected service-fee amount
 * live as they type.
 *
 * Math:  amount = total × rate / 100   (rounded to 2 decimals)
 * Format is fr-FR Euro to match the server's `number_to_currency(...,
 * unit: "€", separator: ",", delimiter: " ", format: "%n %u")` output —
 * the exact same Intl trick used in `moe_fee_assiette_controller.js`.
 *
 * The server STILL re-computes canonically via `Prorata::Calculator`
 * with `BigDecimal#round(2, ROUND_HALF_UP)` on save; this controller's
 * output is purely a UX hint, never persisted.
 *
 * Targets:
 * - input:   the <input> for total_expenses_declared_ht (carries the
 *            `input->prorata-expenses-preview#update` action).
 * - preview: the <dd> in the "Peines et soins" row that displays the
 *            computed amount (or "— €" when empty).
 * - fundsCalled: the <input> for funds_called_ht. Il est CIBLE et pas
 *            seulement porteur d'action, parce que l'état de l'avertissement
 *            doit être calculé au `connect()` : le champ est pré-rempli avec la
 *            valeur déjà déclarée sur le compte, qui peut déjà dépasser le seuil.
 * - fundsOverCallWarning: the wrapper around the warning shown when the
 *            declared funds_called_ht exceeds what the maître d'ouvrage has
 *            actually retained so far. Toggled via a plain `hidden` class —
 *            the node carries NO display utility, which would win the
 *            cascade over `hidden` in Tailwind v4.
 *
 * Values:
 * - rate (Number): service_fee_rate_percent from the ProrataAccount
 *   (passed by ERB as `data-prorata-expenses-preview-rate-value`).
 * - retainedTotal (Number): total_contributions_ht, i.e. everything the
 *   maître d'ouvrage has retained on the project so far (passed by ERB as
 *   `data-prorata-expenses-preview-retained-total-value`). Reference point
 *   only: funds_called_ht is DECLARED and never derived from it.
 *
 * Actions:
 * - update: triggered by `input` on the total field — reads the value,
 *   computes the peines-et-soins amount, writes the formatted result
 *   to the preview target.
 * - checkFundsCalled: triggered by `input` on the funds-called field —
 *   shows a non-blocking hint when the amount exceeds the retained total.
 *   Rejoué au `connect()`, sinon la modale s'ouvre muette sur une valeur déjà
 *   au-dessus du seuil et l'avertissement ne parle qu'à la première frappe
 *   (défaut trouvé en QA).
 */
export default class extends Controller {
  static targets = ['preview', 'fundsCalled', 'fundsOverCallWarning'];
  static values = {
    rate: { type: Number, default: 0 },
    retainedTotal: { type: Number, default: 0 },
  };

  // La modale est rendue avec la page et reconnectée à chaque ouverture : l'état
  // de l'avertissement doit refléter le champ tel qu'il est, pas attendre une
  // frappe. Même geste que `updateManagerRetentionState()` du wizard.
  connect() {
    this.#refreshFundsCalledWarning();
  }

  update(event) {
    if (!this.hasPreviewTarget) return;

    const raw = event.target.value;
    const total = parseFloat(raw);

    if (!Number.isFinite(total) || total <= 0) {
      this.previewTarget.textContent = '— €';
      return;
    }

    const amount = Math.round(((total * this.rateValue) / 100) * 100) / 100;
    this.previewTarget.textContent = this.#formatCurrency(amount);
  }

  // Aide à la saisie, jamais bloquante : le maître d'ouvrage peut légitimement
  // avoir versé plus qu'il n'a retenu, et la différence dort chez lui jusqu'au
  // DGD. Aucune validation serveur ne s'y adosse.
  checkFundsCalled() {
    this.#refreshFundsCalledWarning();
  }

  // Lit la CIBLE et non `event.target` : c'est ce qui rend l'appel du
  // `connect()` possible, là où il n'y a pas d'événement.
  #refreshFundsCalledWarning() {
    if (!this.hasFundsOverCallWarningTarget || !this.hasFundsCalledTarget) return;

    const called = parseFloat(this.fundsCalledTarget.value);
    const over = Number.isFinite(called) && called > this.retainedTotalValue;
    this.fundsOverCallWarningTarget.classList.toggle('hidden', !over);
  }

  #formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }
}
